import { createSelector } from 'reselect';
import {
  compose,
  sortBy,
  every,
  filter,
  flow,
  get,
  includes,
  identity,
  isNull,
  map,
  nth,
  orderBy,
  toLower
} from 'lodash/fp';

import { IN_PROGRESS } from '../constants/challengeStatusTypes';
import { addRankToPlayers } from '../utils/playerUtils';
import playersSelector from './playersSelector';
import teamsSelector from './teamsSelector';

const annotateRanks = addRankToPlayers('score');

function getFilterAndSort({
  gamesFilters = [],
  playerTypeFilters = [],
  positionFilter,
  positionFilters,
  gameFilter,
  statusFilters = [],
  searchTerm,
  sortField,
  sortDirection,
  teamsFilters = [],
  teamsSelectedFilters = null
}) {
  const selectable = filter(x => x.positions.length > 0);
  const filterByPosition = positionFilter
    ? filter(x => x.positions.find(p => positionFilter === p.key))
    : identity;
  const filterByPositions =
    Array.isArray(positionFilters) && positionFilters.length > 0
      ? filter(x => x.positions.find(p => positionFilters.includes(p.key)))
      : identity;
  const filterByGame = gameFilter ? filter(x => x.gameId === gameFilter) : identity;
  const filterByGames =
    gamesFilters.length > 0 ? filter(x => includes(x.gameId, gamesFilters)) : identity;
  const filterByTeams =
    teamsFilters.length > 0 ? filter(x => includes(x.teamId, teamsFilters)) : identity;
  const filterByTeamsSelected = Array.isArray(teamsSelectedFilters)
    ? filter(x => includes(x.teamId, teamsSelectedFilters))
    : identity;
  const filterByStatus =
    statusFilters.length > 0 ? filter(x => includes(x.statusId, statusFilters)) : identity;
  // exclude teams from filterByTypes as all we have currently is 'hide inactive players';
  const filterByTypes = filter(player =>
    !player.isTeam ? every(x => player[x], playerTypeFilters) : identity
  );
  const filterBySearchTerm = filter(flow(get('name'), toLower, includes(toLower(searchTerm))));
  const sortEntities = orderBy(e => {
    return get(sortField, e) === undefined || isNull(get(sortField, e)) ? -1 : get(sortField, e);
  }, sortDirection);

  if (searchTerm) {
    return compose(selectable, filterBySearchTerm, sortEntities);
  }

  return compose(
    selectable,
    filterByPosition,
    filterByPositions,
    filterByGame,
    filterByGames,
    filterByTeams,
    filterByTeamsSelected,
    filterByStatus,
    filterByTypes,
    sortEntities
  );
}

const annotateValueForMoney = compose(
  sorted => {
    const minValueForMoney = compose(get('valueForMoney'), nth(1))(sorted); // To prevent one player from throwing off the entire graph we treat the 2nd top as the max and 2nd bottom as the min.
    const maxValueForMoney = compose(get('valueForMoney'), nth(-2))(sorted);
    return map(entity => ({
      ...entity,
      minValueForMoney,
      maxValueForMoney
    }))(sorted);
  },
  sortBy('valueForMoney'),
  map(entity => ({
    ...entity,
    valueForMoney: entity.salary > 0 ? (1000000 * entity.score) / entity.salary : undefined
  }))
);

function getEntities(
  players,
  teams,
  positionFilter,
  positionFilters,
  gameFilter,
  playerTypeFilters,
  gamesFilters,
  teamsFilters,
  teamsSelectedFilters,
  statusFilters,
  searchTerm,
  sortField,
  sortDirection,
  challengeStatusId,
  isCruncher = false
) {
  // attach ranks and valueForMoney for LIVE / COMPLETED challenges only
  // sorting large arrays in javascript is expensive!!
  const entities =
    challengeStatusId >= IN_PROGRESS
      ? compose(annotateRanks, annotateValueForMoney)([...players, ...teams])
      : [...players, ...teams];

  if (isCruncher) {
    return teams.length
      ? getFilterAndSort({
          teamsSelectedFilters
        })(entities)
      : entities;
  }

  const filterAndSort = getFilterAndSort({
    positionFilter,
    positionFilters,
    gameFilter,
    playerTypeFilters,
    gamesFilters,
    teamsFilters,
    teamsSelectedFilters,
    statusFilters,
    searchTerm,
    sortField,
    sortDirection
  });

  return filterAndSort(entities);
}

const entitiesSelector = createSelector(
  playersSelector,
  teamsSelector,
  get('lineUp.positionFilter'),
  get('lineUp.positionFilters'),
  get('lineUp.gameFilter'),
  get('playerList.playerTypeFilters'),
  get('playerList.filteredGames'),
  get('playerList.filteredTeams'),
  get('playerList.selectedTeams'),
  get('playerList.gameStatusFilters'),
  get('playerList.searchTerm'),
  get('challenge.sortField'),
  get('challenge.sortDirection'),
  get('challenge.statusId'),
  (state, { isCruncher } = {}) => isCruncher,
  getEntities
);

export default entitiesSelector;
